import React from "react";

/* The frame every card in the conversation shares.
 *
 * Lifted out of CopilotApprovalCard, where it lived as a local `Shell`, because
 * the proposal flow adds seven more cards to this transcript. Seven copies of a
 * header gradient and a border is seven places to fix a radius — and the way
 * that failure shows up is subtle: cards that are almost the same, which reads
 * as sloppiness long before anyone can say which one is wrong.
 *
 * Three slots, because that is what the cards actually have:
 *
 *   header  icon + title, and an optional `meta` pinned right (a count, a live
 *           badge, a step marker). The title is the card's claim; meta is its
 *           current state.
 *   body    whatever the card is.
 *   footer  actions, separated by a rule and sitting on a tinted ground so a
 *           decision never looks like part of the content it follows.
 *
 * `footer` is a slot rather than a prop of buttons: the cards' actions differ
 * too much (one primary, three-way, none at all) for a shared button API to be
 * anything but a worse version of writing them.
 */

/* Literal classes, not `max-w-[${w}px]`. Tailwind emits only the class names it
 * can see in source, so an interpolated width silently produces no rule at all
 * and the card renders full-bleed. Two sizes cover the product: a card you read
 * at a glance, and one holding a list. */
const WIDTHS = {
  520: "max-w-[520px]",
  640: "max-w-[640px]",
};

const CopilotCardShell = ({
  icon,
  title,
  meta,
  footer,
  width = 520,
  className = "",
  children,
}) => (
  <div
    className={`w-full ${WIDTHS[width] || WIDTHS[520]} overflow-hidden rounded-2xl border border-[#A04A97]/25 bg-white shadow-[0_2px_16px_-8px_rgba(24,16,28,0.18)] animate-copilot-rise motion-reduce:animate-none ${className}`}
  >
    <div className="flex items-center gap-2 border-b border-[#18101C]/[0.06] bg-gradient-to-r from-[#A04A97]/[0.07] to-[#C86B4F]/[0.05] px-4 py-2.5">
      {icon && (
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#A04A97]/12 text-[#A04A97]">
          {icon}
        </span>
      )}
      <p className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[#18101C]">
        {title}
      </p>
      {meta && <div className="flex-none">{meta}</div>}
    </div>

    {children}

    {footer && (
      <div className="flex flex-wrap items-center gap-2 border-t border-[#18101C]/[0.06] bg-[#18101C]/[0.015] px-4 py-3">
        {footer}
      </div>
    )}
  </div>
);

export default CopilotCardShell;
